// src/components/DashboardGroups/ExpandedChildren.tsx
import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { ItemTypes } from '@/components/Dashboard/dndTypes';
import type { CardDragItem } from '@/components/Dashboard/dndTypes';
import Nestable from 'react-nestable';
import 'react-nestable/dist/styles/index.css';
import ConversationItem from '@/components/ConversationItem/ConversationItem';
import type { ConversationDetail } from '@/types/conversation';
import type { SideMenuItem } from '@/services/menuService';
import type { ApiConfig, AppConfig } from '@/components/ConversationItem/types';
import type { NestableItem } from './treeUtils';
import type { CardViewMode } from './types';
import { buildChildTree } from './treeUtils';
import { setCardBeingDragged, getCurrentDashboardId } from '@/lib/cardDragState';
import { apiService } from '@/services/api';
import { socketService } from '@/services/socketService';

interface ExpandedChildrenProps {
  parentId: number;
  groupId: number;
  children: ConversationDetail[];
  maxDepth: number;
  cardViewMode: CardViewMode;
  columnWidth: number;
  onNestableChange?: (params: {
    items: NestableItem[];
    dragItem: NestableItem;
    targetPath: number[];
    parentId: number;
    groupId: number;
  }) => void;
  expandedParentIds?: Set<number>;
  onToggleExpandParent?: (conversationId: number, groupId: number) => void;
  // Props needed to render ConversationItem
  api: ApiConfig & {
    refreshToken: string;
    ExternalData: string;
    SocketServer: string;
    TenancyName: string;
    PersonId: string;
  };
  config: AppConfig & {
    buildVersion?: string;
    webChaturl?: string;
    appPrefix?: string;
  };
  storageData: any;
  realItemTypes: any;
  configMenus: any;
  userData: any;
  hideProjectName?: boolean;
  onOpenInfoWindow?: (conversation: ConversationDetail) => void;
  onEditConversation?: (e: any, conversation: ConversationDetail) => void;
  onCreateConversation?: (conversation: ConversationDetail, itemType: SideMenuItem) => void;
  onUpdateConversation?: (conversationId: number, updates: Partial<ConversationDetail>) => void;
  onDeleteConversation?: (conversationId: number) => void;
  onArchiveConversation?: (conversationId: number) => void;
  onRestoreConversation?: (conversationId: number) => void;
  onDeleteConversationPermanently?: (conversationId: number) => void;
  onDuplicateItem?: (e: any, conversation: ConversationDetail) => void;
  recycleBin?: boolean;
  onMakeChild?: (
    childConversationId: number,
    parentConversationId: number,
    groupId: number,
    sourceGroupId: number
  ) => void;
  isGroupDefault?: boolean;
  selectedCardIds?: Set<number>;
  isMultiDragActive?: boolean;
  onCardCtrlClick?: (cardId: number, shiftKey: boolean, groupId: number) => void;
  onCardShiftClick?: (cardId: number, groupId: number) => void;
  getSelectedConversations?: () => ConversationDetail[];
}

// Wrapper for individual nested items to handle react-dnd drag/drop zones for multi-level hierarchy
const NestedChildWrapper = React.memo<{
  itemProps: any;
  conversation: ConversationDetail;
  groupId: number;
  isExpanded: boolean;
  onToggleExpandParent?: (conversationId: number, groupId: number) => void;
  onMakeChild?: (
    childConversationId: number,
    parentConversationId: number,
    groupId: number,
    sourceGroupId: number
  ) => void;
  isGroupDefault?: boolean;
  isDetailed?: boolean;
  isSelected?: boolean;
  selectedCount?: number;
  isMultiDragActive?: boolean;
  onCtrlClick?: (cardId: number, shiftKey: boolean, groupId: number) => void;
  onShiftClick?: (cardId: number, groupId: number) => void;
  getSelectedConversations?: () => ConversationDetail[];
    }>(function NestedChildWrapper({
      itemProps,
      conversation,
      groupId,
      isExpanded,
      onToggleExpandParent,
      onMakeChild,
      isGroupDefault = false,
      isDetailed = false,
      isSelected = false,
      selectedCount = 0,
      isMultiDragActive = false,
      onCtrlClick,
      onShiftClick,
      getSelectedConversations,
    })
    {
      const cardRef = useRef<HTMLDivElement>(null);

      // Draggable — allows nested cards to be dragged out of their parent
      const [{ isDragging }, drag, preview] = useDrag<CardDragItem, void, { isDragging: boolean }>({
        type: ItemTypes.CARD,
        item: () =>
        {
          setCardBeingDragged({
            conversationId: conversation.ConversationId,
            groupId,
            dashboardId: getCurrentDashboardId(),
            configValueId: conversation.ConfigValueId,
          });
          window.dispatchEvent(
            new CustomEvent('corgee:card-drag-start', {
              detail: {
                conversationId: conversation.ConversationId,
                groupId,
                dashboardId: getCurrentDashboardId(),
              },
            }),
          );
          return {
            type: ItemTypes.CARD,
            id: conversation.ConversationId,
            columnId: groupId,
            parentId: conversation.ParentConversationId ?? undefined,
          };
        },
        end: () =>
        {
          setCardBeingDragged(null);
          window.dispatchEvent(new CustomEvent('corgee:card-drag-end'));
        },
        collect: monitor => ({ isDragging: monitor.isDragging() }),
      });

      // Single drop target on the whole card (same pattern as DndTestPage / SortableCard).
      // Entire card = make-child zone for nested cards (no reorder needed here).
      const [{ isOverChildZone }, drop] = useDrop<CardDragItem, void, { isOverChildZone: boolean }>({
        accept: ItemTypes.CARD,
        collect: monitor => ({ isOverChildZone: monitor.isOver({ shallow: true }) }),
        canDrop: item => !isGroupDefault && item.id !== conversation.ConversationId,
        drop: (item, monitor) =>
        {
          if (monitor.didDrop()) return;
          onMakeChild?.(item.id, conversation.ConversationId, groupId, item.columnId);
        },
      });

      // Single ref for both drag and drop — avoids separate zone flickering
      drag(drop(cardRef));

      // Hide the native drag ghost in favor of the multi-drag overlay
      if (isSelected && selectedCount > 1)
      {
        preview(getEmptyImage());
      }

      // Stable refs for the callback values
      const onCtrlClickRef = useRef(onCtrlClick);
      const onShiftClickRef = useRef(onShiftClick);
      const conversationIdRef = useRef(conversation.ConversationId);
      onCtrlClickRef.current = onCtrlClick;
      onShiftClickRef.current = onShiftClick;
      conversationIdRef.current = conversation.ConversationId;

      // Ctrl/Shift+Click selection, same pattern as SortableCard
      useEffect(() =>
      {
        const el = cardRef.current;
        if (!el) return;

        const handlePointerDown = (e: PointerEvent) =>
        {
          if (e.ctrlKey || e.metaKey)
          {
            e.preventDefault();
            e.stopImmediatePropagation();
            onCtrlClickRef.current?.(conversationIdRef.current, e.shiftKey, groupId ?? 0);
          }
          else if (e.shiftKey)
          {
            e.preventDefault();
            e.stopImmediatePropagation();
            onShiftClickRef.current?.(conversationIdRef.current, groupId ?? 0);
          }
        };

        const handleClick = (e: MouseEvent) =>
        {
          if (e.ctrlKey || e.metaKey || e.shiftKey)
          {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        };

        el.addEventListener('pointerdown', handlePointerDown, true);
        el.addEventListener('click', handleClick, true);
        return () =>
        {
          el.removeEventListener('pointerdown', handlePointerDown, true);
          el.removeEventListener('click', handleClick, true);
        };
      }, [groupId]);

      const isHiddenMultiDrag = !isDragging && isSelected && isMultiDragActive;

      const style: React.CSSProperties = {
        opacity: isDragging ? 0.3 : undefined,
        zIndex: isDragging ? 1000 : undefined,
      };

      const selectionClass = isHiddenMultiDrag
        ? 'dg-card-multi-hidden'
        : isSelected
          ? 'dg-card-selected'
          : '';

      return (
        <div
          ref={cardRef}
          style={style}
          className={`dg-nested-card ${isDetailed ? 'dg-nested-card-detailed' : ''} ${selectionClass} ${isOverChildZone ? 'dg-card-hierarchy-target' : ''}`}
        >
          {conversation.IsParent && (
            <span
              className={`dg-nestable-collapse-icon expandable-control${isExpanded ? ' expanded' : ''}`}
              onClick={e =>
              {
                e.stopPropagation();
                e.preventDefault();
                onToggleExpandParent?.(conversation.ConversationId, groupId);
              }}
              onPointerDown={e => e.stopPropagation()}
              title={isExpanded ? 'Collapse children' : 'Expand children'}
            />
          )}
          <ul className="list-none p-0 m-0">
            <ConversationItem
              {...itemProps}
              isMultiple={isSelected && selectedCount > 1}
              getSelectedConversations={getSelectedConversations}
            />
          </ul>
        </div>
      );
    });

export const ExpandedChildren: React.FC<ExpandedChildrenProps> = React.memo(
  function ExpandedChildren({
    parentId,
    groupId,
    children,
    maxDepth,
    cardViewMode,
    columnWidth,
    onNestableChange,
    api,
    config,
    storageData,
    realItemTypes,
    configMenus,
    userData,
    hideProjectName,
    onOpenInfoWindow,
    onEditConversation,
    onCreateConversation,
    onUpdateConversation,
    onDeleteConversation,
    onArchiveConversation,
    onRestoreConversation,
    onDeleteConversationPermanently,
    onDuplicateItem,
    expandedParentIds,
    onToggleExpandParent,
    recycleBin: _recycleBin = false,
    onMakeChild,
    isGroupDefault = false,
    selectedCardIds,
    isMultiDragActive = false,
    onCardCtrlClick,
    onCardShiftClick,
    getSelectedConversations,
  })
  {
    const hasItemCreationPermission =
      localStorage.getItem('C2_hasItemCreationPermission') === 'true';

    const childrenRef = useRef(children);
    childrenRef.current = children;

    const handleAddLayerToCard = useCallback(
      async (
        convId: number,
        _e: React.MouseEvent | MouseEvent,
        layer: any,
        functions?: { onUpdateLayerToCard?: (layerId: number) => void },
      ) =>
      {
        const conv = childrenRef.current?.find(c => c.ConversationId === convId);
        if (!conv) return;

        const alreadyPresent = (conv.ConversationLayers || []).some(
          (cl: any) => cl.LayerId === layer.LayerId,
        );

        if (alreadyPresent)
        {
          functions?.onUpdateLayerToCard?.(layer.LayerId);
          return;
        }

        const newLayerName = (layer as any).Name || layer.LayerName || '';
        const newLayerColor = (layer as any).Color || layer.LayerColor || '';
        const updatedLayers = [
          ...(conv.ConversationLayers || []),
          {
            ConversationId: conv.ConversationId,
            LayerId: layer.LayerId,
            LayerName: newLayerName,
            Name: newLayerName,
            LayerColor: newLayerColor,
            Color: newLayerColor,
            isForAdd: true,
          },
        ];

        try
        {
          const response = await apiService.put<any[]>(
            'conversation/UpdateLayers',
            { ...conv, ConversationLayers: updatedLayers, SocketConnectionId: socketService.connectionId },
          );

          const rawLayers = Array.isArray(response) && response.length > 0 ? response : updatedLayers;
          const existingLayerMap = new Map((conv.ConversationLayers || []).map((l: any) => [l.LayerId, l]));
          const finalLayers = rawLayers.map((l: any) =>
          {
            if (l.Name || l.LayerName) return l;
            const src = existingLayerMap.get(l.LayerId);
            if (src)
            {
              return {
                ...l,
                Name: src.Name || src.LayerName || '',
                LayerName: src.LayerName || src.Name || '',
                LayerColor: l.LayerColor || src.LayerColor || src.Color || '',
                Color: l.Color || src.Color || src.LayerColor || '',
              };
            }
            if (l.LayerId === layer.LayerId)
            {
              return { ...l, Name: newLayerName, LayerName: newLayerName, LayerColor: newLayerColor, Color: newLayerColor };
            }
            return l;
          });

          onUpdateConversation?.(convId, { ConversationLayers: finalLayers, isAddLayerToCard: true } as any);
          functions?.onUpdateLayerToCard?.(layer.LayerId);
        }
        catch (err)
        {
          console.error('Failed to add layer to card:', err);
        }
      },
      [onUpdateConversation],
    );

    const handleAddLayerToProject = useCallback(
      async (
        convId: number,
        _e: React.MouseEvent | MouseEvent,
        layer: any,
        functions?: { onUpdateLayerToCard?: (layerId: number) => void; skipCardUpdate?: boolean },
      ) =>
      {
        const conv = childrenRef.current?.find(c => c.ConversationId === convId);
        if (!conv) return;

        const layerName = (layer as any).Name || layer.LayerName || '';
        const layerColor = (layer as any).Color || layer.LayerColor || '';
        let projectConversationId = (layer as any).ProjectConversationId;

        if (!projectConversationId && conv.ProjectId)
        {
          try
          {
            const proj = await apiService.get<any>(
              `Project/GetProject?projectId=${conv.ProjectId}`,
            );
            projectConversationId = proj?.ConversationId;
          }
          catch (_) { /* best-effort */ }
        }

        if (projectConversationId)
        {
          try
          {
            const projectConv = await apiService.post<any>(
              `conversation/GetConversationById?conversationId=${projectConversationId}`,
              null,
            );
            if (projectConv && projectConv.ConfigValueId !== 15)
            {
              let groups: any[] = [];
              try
              {
                const fetchedGroups = await apiService.get<any[]>(
                  `group/GetGroupsByProject?projectId=${projectConv.ProjectId}`,
                );
                if (Array.isArray(fetchedGroups)) groups = fetchedGroups;
              }
              catch (_) { /* best-effort */ }

              await apiService.post<any>('conversation/updateconversation', {
                ...projectConv,
                CustomFields: [],
                ConversationGroups: groups.length > 0 ? groups : projectConv.ConversationGroups,
                ConversationLayers: [
                  ...(projectConv.ConversationLayers || []),
                  {
                    ConversationId: projectConv.ConversationId,
                    LayerId: layer.LayerId,
                    LayerName: layerName,
                    LayerColor: layerColor,
                    isForAdd: true,
                    IsActive: 1,
                  },
                ],
                SocketConnectionId: socketService.connectionId,
              });
            }
          }
          catch (err)
          {
            console.error('Failed to add layer to project conversation:', err);
          }
        }

        if (functions?.skipCardUpdate)
        {
          functions.onUpdateLayerToCard?.(layer.LayerId);
          return;
        }

        try
        {
          const alreadyPresent = (conv.ConversationLayers || []).some(
            (cl: any) => cl.LayerId === layer.LayerId,
          );

          if (!alreadyPresent)
          {
            const updatedLayers = [
              ...(conv.ConversationLayers || []),
              {
                ConversationId: conv.ConversationId,
                LayerId: layer.LayerId,
                LayerName: layerName,
                Name: layerName,
                LayerColor: layerColor,
                Color: layerColor,
                isForAdd: true,
              },
            ];

            const response = await apiService.put<any[]>(
              'conversation/UpdateLayers',
              { ...conv, ConversationLayers: updatedLayers, SocketConnectionId: socketService.connectionId },
            );

            const rawLayers = Array.isArray(response) && response.length > 0 ? response : updatedLayers;
            const existingLayerMap = new Map((conv.ConversationLayers || []).map((l: any) => [l.LayerId, l]));
            const finalLayers = rawLayers.map((l: any) =>
            {
              if (l.Name || l.LayerName) return l;
              const src = existingLayerMap.get(l.LayerId);
              if (src)
              {
                return {
                  ...l,
                  Name: src.Name || src.LayerName || '',
                  LayerName: src.LayerName || src.Name || '',
                  LayerColor: l.LayerColor || src.LayerColor || src.Color || '',
                  Color: l.Color || src.Color || src.LayerColor || '',
                };
              }
              if (l.LayerId === layer.LayerId)
              {
                return { ...l, Name: layerName, LayerName: layerName, LayerColor: layerColor, Color: layerColor };
              }
              return l;
            });

            onUpdateConversation?.(convId, { ConversationLayers: finalLayers, isAddLayerToCard: true } as any);
          }

          functions?.onUpdateLayerToCard?.(layer.LayerId);
        }
        catch (err)
        {
          console.error('Failed to add layer to current conversation:', err);
        }
      },
      [onUpdateConversation],
    );

    // Build tree from flat children list and prune unexpanded nodes
    const treeItems = useMemo(() =>
    {
      const fullTree = buildChildTree(children);

      const prune = (nodes: NestableItem[]) =>
      {
        for (const node of nodes)
        {
          if (!expandedParentIds?.has(node.id))
          {
            node.children = [];
          }
          else
          {
            prune(node.children);
          }
        }
      };
      prune(fullTree);

      return fullTree;
    }, [children, expandedParentIds]);

    // Build props for a conversation item
    const buildItemProps = useCallback(
      (conversation: ConversationDetail, idx: number) =>
      {
        const isDetailed = cardViewMode === 'detailed';
        return {
          index: idx,
          conversation,
          api,
          isViewCard: isDetailed,
          hasArchivedFilter: false,
          isConversationDetailsView: false,
          storageData,
          configMenus,
          config,
          realItemTypes,
          userData,
          columnWidth,
          hideProjectName,
          HasItemCreationPermission: hasItemCreationPermission,
          ShowMoveto: true,
          corgeeFunctions: {
            onSendError: () => {},
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
            openInfoWindow: (conv: ConversationDetail) =>
            {
              onOpenInfoWindow?.(conv);
            },
            editConversation: (e: any, conv: ConversationDetail) =>
            {
              onEditConversation?.(e, conv);
            },
            createConversation: (conv: ConversationDetail, cardType: SideMenuItem) =>
            {
              onCreateConversation?.(conv, cardType);
            },
            deleteConversation: (_e: any, conv: ConversationDetail) =>
            {
              onDeleteConversation?.(conv.ConversationId);
            },
            archiveConversation: (_e: any, conv: ConversationDetail) =>
            {
              onArchiveConversation?.(conv.ConversationId);
            },
            restoreConversation: (conv: ConversationDetail) =>
            {
              onRestoreConversation?.(conv.ConversationId);
            },
            deleteConversationPermanently: (conv: ConversationDetail) =>
            {
              onDeleteConversationPermanently?.(conv.ConversationId);
            },
            onDuplicateItem: (e: any, conv: ConversationDetail) =>
            {
              onDuplicateItem?.(e, conv);
            },
          },
          onUpdateConversation: (convId: number, updates: Partial<ConversationDetail>) =>
          {
            onUpdateConversation?.(convId, updates);
          },
          onAddLayerToCard: (e: any, layer: any, functions?: any) =>
            handleAddLayerToCard(conversation.ConversationId, e, layer, functions),
          onAddLayerToProject: (e: any, layer: any, functions?: any) =>
            handleAddLayerToProject(conversation.ConversationId, e, layer, functions),
        };
      },
      [
        cardViewMode,
        api,
        config,
        storageData,
        configMenus,
        realItemTypes,
        userData,
        columnWidth,
        hideProjectName,
        hasItemCreationPermission,
        onOpenInfoWindow,
        onEditConversation,
        onUpdateConversation,
        onDeleteConversation,
        onArchiveConversation,
        onRestoreConversation,
        onDeleteConversationPermanently,
        onDuplicateItem,
        handleAddLayerToCard,
        handleAddLayerToProject,
      ],
    );

    // Handle nestable changes
    const handleChange = useCallback(
      (params: { items: any[]; dragItem: any; targetPath: number[] }) =>
      {
        if (onNestableChange)
        {
          onNestableChange({
            items: params.items,
            dragItem: params.dragItem,
            targetPath: params.targetPath,
            parentId,
            groupId,
          });
        }
      },
      [onNestableChange, parentId, groupId],
    );

    // Render each nestable item
    const renderItem = useCallback(
      ({
        item,
        index,
      }: {
        item: NestableItem;
        index: number;
        depth: number;
        collapseIcon: React.ReactNode;
      }) =>
      {
        const props = buildItemProps(item.conversation, index);
        return (
          <NestedChildWrapper
            itemProps={props}
            conversation={item.conversation}
            groupId={groupId}
            isExpanded={!!expandedParentIds?.has(item.id)}
            onToggleExpandParent={onToggleExpandParent}
            onMakeChild={onMakeChild}
            isGroupDefault={isGroupDefault}
            isDetailed={cardViewMode === 'detailed'}
            isSelected={selectedCardIds?.has(item.conversation.ConversationId) ?? false}
            selectedCount={selectedCardIds?.size ?? 0}
            isMultiDragActive={isMultiDragActive}
            onCtrlClick={onCardCtrlClick}
            onShiftClick={onCardShiftClick}
            getSelectedConversations={getSelectedConversations}
          />
        );
      },
      [
        buildItemProps,
        groupId,
        expandedParentIds,
        onToggleExpandParent,
        onMakeChild,
        isGroupDefault,
        cardViewMode,
        selectedCardIds,
        isMultiDragActive,
        onCardCtrlClick,
        onCardShiftClick,
        getSelectedConversations,
      ],
    );

    // Render collapse icon natively disabled because we handle it dynamically in NestedChildWrapper
    const renderCollapseIcon = useCallback(() => null, []);

    if (treeItems.length === 0)
    {
      return null;
    }

    return (
      <div className="dg-expanded-children">
        <Nestable
          items={treeItems}
          renderItem={renderItem}
          onChange={handleChange}
          maxDepth={maxDepth}
          collapsed={false}
          threshold={20}
          renderCollapseIcon={renderCollapseIcon}
          disableDrag={true}
        />
      </div>
    );
  },
);

export default ExpandedChildren;
